// todo: review and seprate login into hook + create component, without hardcoded numbers
import { useCallback, useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import { useSelector } from 'react-redux'

import checkIcon from '~/assets/images/sprite/check-icon.svg'
import copyIcon from '~/assets/images/sprite/copy.svg'
import { Styles as S } from '~/pages/download/styles'
import { userApi } from '~/shared/api'
import { StickyButtonContainer } from '~/shared/components/StickyButtonContainer'
import { SvgImage } from '~/shared/components/SvgImage'
import {
  APP_STORE_PPID,
  STORE_DEEP_LINK,
  SUPPORT_EMAIL,
  TEST_ENV_QUERY_PARAM,
} from '~/shared/constants/commonConstants'
import { eventLogger } from '~/shared/services/eventLogger'
import { selectLanguage } from '~/shared/store/common/selects'
import { selectUserEmail, selectUserUUID } from '~/shared/store/user/selects'

import {
  DOWNLOAD_OPTIONS,
  LOGGED_IN_AUTOMATICALLY_OPTIONS,
  downloadDePNG,
  downloadDeWEBP,
  downloadEsPNG,
  downloadEsWEBP,
  downloadFrPNG,
  downloadFrWEBP,
  downloadHuPNG,
  downloadHuWEBP,
  downloadItPNG,
  downloadItWEBP,
  downloadJaPNG,
  downloadJaWEBP,
  downloadKoPNG,
  downloadKoWEBP,
  downloadPNG,
  downloadPlPNG,
  downloadPlWEBP,
  downloadPtPNG,
  downloadPtWEBP,
  downloadTrPNG,
  downloadTrWEBP,
  downloadWEBP,
} from './constants'

const LOCALE_IMAGE_MAP: Record<
  string,
  { png: string; webp: string } | undefined
> = {
  de: { png: downloadDePNG, webp: downloadDeWEBP },
  es: { png: downloadEsPNG, webp: downloadEsWEBP },
  fr: { png: downloadFrPNG, webp: downloadFrWEBP },
  hu: { png: downloadHuPNG, webp: downloadHuWEBP },
  it: { png: downloadItPNG, webp: downloadItWEBP },
  ja: { png: downloadJaPNG, webp: downloadJaWEBP },
  ko: { png: downloadKoPNG, webp: downloadKoWEBP },
  pl: { png: downloadPlPNG, webp: downloadPlWEBP },
  pt: { png: downloadPtPNG, webp: downloadPtWEBP },
  tr: { png: downloadTrPNG, webp: downloadTrWEBP },
}

const Download = () => {
  const userEmail = useSelector(selectUserEmail)
  const uuid = useSelector(selectUserUUID)
  const language = useSelector(selectLanguage)

  const localeImage = LOCALE_IMAGE_MAP[language] ?? {
    png: downloadPNG,
    webp: downloadWEBP,
  }
  const [isCopied, setIsCopied] = useState(false)
  const [hasEverCopied, setHasEverCopied] = useState(false)
  const [deepLink, setDeepLink] = useState(STORE_DEEP_LINK)

  useEffect(() => {
    if (!uuid) return

    const isTestEnvironment =
      document.location.search.includes(TEST_ENV_QUERY_PARAM)

    if (isTestEnvironment) return

    userApi.getAutologinToken(uuid).then((response) => {
      if (response.success && response.data) {
        setDeepLink(
          `${STORE_DEEP_LINK.replace(
            'custom_token',
            response.data.custom_token,
          )}&ppid=${APP_STORE_PPID}`,
        )
      }
    })
  }, [uuid])

  const handleClick = () => {
    eventLogger.logDownloadClicked(() => {
      window.location.href = deepLink
    })
  }

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(userEmail)
    setHasEverCopied(true)
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 2000)
  }, [userEmail])

  useEffect(() => {
    eventLogger.logGettingAppShown()
  }, [])

  return (
    <S.StylesContainer>
      <S.Title>
        <Trans i18nKey="download.title" />
      </S.Title>

      <S.Subtitle>
        <Trans i18nKey="download.subtitle" />
      </S.Subtitle>

      <div>
        {DOWNLOAD_OPTIONS.map((option, index) => (
          <S.DownloadOption key={option}>
            <S.OptionNumber>{index + 1}</S.OptionNumber>
            <S.OptionText>
              <Trans
                i18nKey={option}
                components={{
                  strong: <strong />,
                  button: (
                    <button
                      type="button"
                      data-deep-link={deepLink}
                      onClick={handleClick}
                    >
                      MyGrowth
                    </button>
                  ),
                }}
              />
            </S.OptionText>
          </S.DownloadOption>
        ))}
      </div>

      <S.ImageWrapper>
        <picture>
          <source srcSet={localeImage.webp} type="image/webp" />
          <S.Image src={localeImage.png} alt="" />
        </picture>
      </S.ImageWrapper>

      <S.LoggedInWrapper>
        <S.LoggedInTitle>
          <Trans i18nKey="download.nonLoggedInTitle" />
        </S.LoggedInTitle>

        <S.DownloadOption>
          <S.OptionNumber>1</S.OptionNumber>
          <S.OptionText>
            <Trans i18nKey={LOGGED_IN_AUTOMATICALLY_OPTIONS[0]} />
          </S.OptionText>
        </S.DownloadOption>

        <S.DownloadOption>
          <S.OptionNumber>2</S.OptionNumber>
          <S.OptionText>
            <Trans i18nKey={LOGGED_IN_AUTOMATICALLY_OPTIONS[1]} />
          </S.OptionText>
        </S.DownloadOption>

        <S.CopyEmail>
          <S.UserEmail>
            <S.EmailLabel>
              <Trans i18nKey="actions.email" />
            </S.EmailLabel>
            <S.EmailInput type="text" value={userEmail} readOnly />
          </S.UserEmail>

          <S.CopyButtonWrapper>
            {hasEverCopied && (
              <S.CopiedTooltip $isVisible={isCopied}>
                <Trans i18nKey="download.tooltipTitle" />
                <S.CopiedTooltipIcon />
              </S.CopiedTooltip>
            )}
            <S.CopyButton type="button" onClick={handleCopy}>
              <SvgImage
                svg={isCopied ? checkIcon : copyIcon}
                width={20}
                height={20}
              />
            </S.CopyButton>
          </S.CopyButtonWrapper>
        </S.CopyEmail>

        <S.DownloadOption>
          <S.OptionNumber>3</S.OptionNumber>
          <S.OptionText>
            <Trans
              i18nKey={LOGGED_IN_AUTOMATICALLY_OPTIONS[2]}
              components={{
                supportLink: (
                  <S.SupportLink href={`mailto:${SUPPORT_EMAIL}`}>
                    here
                  </S.SupportLink>
                ),
              }}
            />
          </S.OptionText>
        </S.DownloadOption>
      </S.LoggedInWrapper>

      <StickyButtonContainer>
        <S.StyledButton
          type="button"
          data-deep-link={deepLink}
          onClick={handleClick}
        >
          <Trans i18nKey="download.buttonText" />
        </S.StyledButton>
      </StickyButtonContainer>
    </S.StylesContainer>
  )
}

export default Download
